import { useState, useEffect, useCallback, useMemo } from 'react';
import { User, Session } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';
import { posthog } from '@/lib/posthog';

/**
 * USER AND TEAM MANAGEMENT HOOK
 *
 * This hook manages the relationship between users and teams in a multi-tenant architecture.
 *
 * Key Concepts:
 *   - Users (auth.users + user_profiles): Global user identity and admin status
 *   - Teams (teams table): Customer companies, the primary tenant in multi-tenant architecture
 *   - Team Memberships (team_memberships): Authoritative user-to-team mapping with roles
 *
 * User-Team Relationship:
 *   - Many-to-many: One user can belong to multiple teams
 *   - Each membership has a role: owner, editor, analyst, viewer
 *   - ALWAYS use team_memberships to check team access, NOT user_profiles
 *
 * Roles and Permissions:
 *   - owner: Full control, billing access, can manage members
 *   - editor: Can edit businesses and valuations, can view contact info
 *   - analyst: Can view and analyze data, read-only for most operations
 *   - viewer: Read-only access, cannot view contact info
 *
 * Current Team Selection:
 *   - Stored in user_profiles.last_selected_team_id (UX convenience only)
 *   - Also stored in localStorage for instant loading
 *   - NOT authoritative for access control - always check team_memberships
 *
 * Schema Tables:
 *   - auth.users: Supabase authentication
 *   - user_profiles: Global profile (name, admin flag, last_selected_team_id)
 *   - team_memberships: User-to-team mapping with roles (AUTHORITATIVE)
 *   - teams: Company/team records
 */
interface TeamMembership {
  id: string;
  team_id: string;
  user_id: string;
  role: string;
  joined_at: string;
  teams: {
    id: string;
    name: string;
    company_name?: string;
    company_logo_url?: string;
    company_description?: string;
    booking_page_url?: string;
    stripe_customer_id?: string;
  };
}

interface AuthState {
  user: User | null;
  session: Session | null;
  teams: TeamMembership[];
  currentTeamId: string | null;
  loading: boolean;
  error: string | null;
  signOut: () => Promise<void>;
  setCurrentTeamId: (teamId: string | null) => void;
  refetchTeams: () => Promise<void>;
  getUserRole: (teamId: string) => string | null;
  canAccessContactInfo: (teamId: string) => boolean;
  canEdit: (teamId: string) => boolean;
  canAnalyze: (teamId: string) => boolean;
}

export const useAuthState = (): AuthState => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [teams, setTeams] = useState<TeamMembership[]>([]);
  const [currentTeamId, setCurrentTeamIdState] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [hasLoadedPreference, setHasLoadedPreference] = useState(false);

  // Fetch teams data
  const fetchTeams = useCallback(async () => {
    if (!user?.id) {
      setTeams([]);
      return;
    }

    try {
      const { data, error } = await supabase
        .from('team_memberships')
        .select(`
          *,
          teams (
            id,
            name,
            company_name,
            company_logo_url,
            company_description,
            booking_page_url,
            stripe_customer_id
          )
        `)
        .eq('user_id', user.id);

      if (error) throw error;
      setTeams(data || []);
      setError(null);
    } catch (err) {
      console.error('Error fetching teams:', err);
      setError('Failed to fetch teams');
    }
  }, [user?.id]);

  // Load team preference from database/localStorage
  const loadTeamPreference = useCallback(async () => {
    if (!user) {
      setHasLoadedPreference(true);
      return;
    }
    
    try {
      const { data: profile } = await supabase
        .from('user_profiles')
        .select('last_selected_team_id')
        .eq('user_id', user.id)
        .maybeSingle();

      if (profile?.last_selected_team_id) {
        setCurrentTeamIdState(profile.last_selected_team_id);
        setHasLoadedPreference(true);
        return;
      }
    } catch (error) {
      console.log('Database fetch failed, trying localStorage');
    }
    
    // Fallback to localStorage
    const saved = localStorage.getItem('currentTeamId');
    if (saved) setCurrentTeamIdState(saved);
    setHasLoadedPreference(true);
  }, [user]);

  // Persist team selection
  const persistTeamSelection = useCallback(async (teamId: string | null) => {
    if (teamId) {
      localStorage.setItem('currentTeamId', teamId);
      
      if (user) {
        try {
          await supabase
            .from('user_profiles')
            .upsert({
              user_id: user.id,
              last_selected_team_id: teamId
            }, {
              onConflict: 'user_id'
            });
        } catch (error) {
          console.log('Failed to save team preference to database:', error);
        }
      }
    } else {
      localStorage.removeItem('currentTeamId');
    }
  }, [user]);

  // Set current team with persistence
  const setCurrentTeamId = useCallback((teamId: string | null) => {
    console.log('useAuthState: Setting currentTeamId to:', teamId);
    setCurrentTeamIdState(teamId);
    persistTeamSelection(teamId);
  }, [persistTeamSelection]);

  // Add storage event listener to sync across any remaining instances
  useEffect(() => {
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === 'currentTeamId' && e.newValue !== null) {
        const newTeamId = e.newValue;
        if (newTeamId !== currentTeamId) {
          console.log('useAuthState: Syncing currentTeamId from storage:', newTeamId);
          setCurrentTeamIdState(newTeamId);
        }
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, [currentTeamId]);

  // Initialize auth state (runs only once on mount)
  useEffect(() => {
    let mounted = true;

    // Check for existing session immediately
    supabase.auth.getSession().then(({ data: { session } }) => {
      if (mounted) {
        setSession(session);
        setUser(session?.user ?? null);
        setLoading(false);
      }
    });

    // Set up auth state listener
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        if (mounted) {
          setSession(session);
          setUser(session?.user ?? null);

          // Identify user in PostHog
          if (session?.user && posthog.__loaded) {
            posthog.identify(session.user.id, {
              email: session.user.email,
            });
          } else if (!session?.user && posthog.__loaded) {
            posthog.reset();
          }

          // Don't set loading to false here to prevent flicker on navigation
        }
      }
    );

    return () => {
      mounted = false;
      subscription.unsubscribe();
    };
  }, []);

  // Load team preference when user changes
  useEffect(() => {
    if (user) {
      loadTeamPreference();
    } else {
      setHasLoadedPreference(false);
    }
  }, [user, loadTeamPreference]);

  // Fetch teams when user changes
  useEffect(() => {
    fetchTeams();

    // Set up real-time subscription for team changes
    if (user?.id) {
      const subscription = supabase
        .channel('team-memberships-changes')
        .on(
          'postgres_changes',
          {
            event: '*',
            schema: 'public',
            table: 'team_memberships',
            filter: `user_id=eq.${user.id}`
          },
          () => {
            fetchTeams();
          }
        )
        .subscribe();

      return () => {
        subscription.unsubscribe();
      };
    }
  }, [fetchTeams, user?.id]);

  // Auto-select first team if none selected (only after loading preference)
  useEffect(() => {
    if (!teams || teams.length === 0 || !hasLoadedPreference) return;

    if (!currentTeamId) {
      setCurrentTeamId(teams[0].team_id);
      return;
    }

    // Check if current team exists in available teams
    const exists = teams.some(t => t.team_id === currentTeamId);
    if (!exists) {
      setCurrentTeamId(teams[0].team_id);
    }
  }, [teams, currentTeamId, setCurrentTeamId, hasLoadedPreference]);

  // Sign out function
  const signOut = useCallback(async () => {
    const { error } = await supabase.auth.signOut();
    if (error) {
      console.error('Error signing out:', error);
    }
  }, []);

  // Helper functions for role-based access
  const getUserRole = useCallback((teamId: string): string | null => {
    const membership = teams.find(t => t.team_id === teamId);
    return membership?.role || null;
  }, [teams]);

  const canAccessContactInfo = useCallback((teamId: string): boolean => {
    const role = getUserRole(teamId);
    return role === 'owner' || role === 'editor';
  }, [getUserRole]);

  const canEdit = useCallback((teamId: string): boolean => {
    const role = getUserRole(teamId);
    return role === 'owner' || role === 'editor';
  }, [getUserRole]);

  const canAnalyze = useCallback((teamId: string): boolean => {
    const role = getUserRole(teamId);
    return role === 'owner' || role === 'editor' || role === 'analyst';
  }, [getUserRole]);

  return useMemo(() => ({
    user,
    session,
    teams,
    currentTeamId,
    loading,
    error,
    signOut,
    setCurrentTeamId,
    refetchTeams: fetchTeams,
    getUserRole,
    canAccessContactInfo,
    canEdit,
    canAnalyze,
  }), [
    user,
    session,
    teams,
    currentTeamId,
    loading,
    error,
    signOut,
    setCurrentTeamId,
    fetchTeams,
    getUserRole,
    canAccessContactInfo,
    canEdit,
    canAnalyze,
  ]);
};